// src/components/PageHeader.tsx

/**
 * Shared page header component for the Quantik Mind enterprise frontend.
 *
 * This component is responsible for:
 * - rendering optional breadcrumb navigation
 * - displaying the page icon, title, and subtitle
 * - showing an optional status badge
 * - exposing an optional actions area
 * - providing a consistent visual header across application pages
 */

import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

/**
 * Breadcrumb item displayed in the page header.
 */
type BreadcrumbItem = {
  /** User-facing breadcrumb label. */
  label: string;

  /** Optional navigation target. */
  href?: string;
};

/**
 * Props accepted by the shared page header component.
 */
type PageHeaderProps = {
  /** Icon displayed in the header visual container. */
  icon: React.ReactElement;

  /** Main page title. */
  title: string;

  /** Optional subtitle displayed below the title. */
  subtitle?: string;

  /** Optional breadcrumb trail displayed above the header. */
  breadcrumbs?: BreadcrumbItem[];

  /** Optional badge displayed next to the title. */
  badge?: string;

  /** Optional action area displayed on the right side of the header. */
  actions?: React.ReactNode;
};

/**
 * Render the shared page header used across enterprise pages.
 *
 * @param props - Page header configuration.
 * @returns The shared page header component.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  icon,
  title,
  subtitle,
  breadcrumbs,
  badge,
  actions,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Optional breadcrumb navigation. */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
              sx={{ color: 'text.secondary' }}
            />
          }
          sx={{ mb: 2 }}
        >
          <Link
            href="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                color: '#39ff14',
              },
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Home
          </Link>

          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;

            return isLast ? (
              <Typography
                key={index}
                sx={{
                  color: '#39ff14',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                href={crumb.href || '#'}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: '#39ff14',
                  },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
      >
        {/* Left section: icon, title, subtitle, and optional badge. */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              background:
                'linear-gradient(135deg, rgba(57, 255, 20, 0.15) 0%, rgba(0, 180, 255, 0.15) 100%)',
              border: '1px solid rgba(57, 255, 20, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(57, 255, 20, 0.2)',
              transition: 'all 0.3s ease',
            }}
          >
            {React.cloneElement(
              icon,
              {
                sx: {
                  fontSize: 32,
                  color: '#39ff14',
                  filter: 'drop-shadow(0 0 8px rgba(57, 255, 20, 0.6))',
                },
              } as any
            )}
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#E6EDF3',
                  letterSpacing: '-0.02em',
                  fontSize: '1.5rem',
                }}
              >
                {title}
              </Typography>

              {badge && (
                <Chip
                  label={badge}
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: 'rgba(57, 255, 20, 0.15)',
                    color: '#39ff14',
                    border: '1px solid rgba(57, 255, 20, 0.3)',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Stack>

            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                  fontSize: '0.95rem',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Right section: optional page-level actions. */}
        {actions && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {actions}
          </Box>
        )}
      </Stack>

      {/* Decorative separator line. */}
      <Box
        sx={{
          mt: 3,
          height: 2,
          background:
            'linear-gradient(90deg, rgba(57, 255, 20, 0.5) 0%, rgba(0, 180, 255, 0.3) 50%, transparent 100%)',
          borderRadius: 1,
        }}
      />
    </Box>
  );
};

export default PageHeader;